import { useTranslation } from '@/i18n/hook';
import { Link } from 'react-router-dom';

export default function FeatureSections() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const reviewRows = currentLang === 'en'
    ? [
        { factor: 'Platform', check: 'Features and operation' },
        { factor: 'Products', check: 'Current information' },
        { factor: 'QC', check: 'Photos and inspection' },
        { factor: 'Warehouse', check: 'Storage' },
        { factor: 'Shipping', check: 'Methods and costs' },
        { factor: 'Support', check: 'Current conditions' },
        { factor: 'Safety', check: 'Domain and payment' },
      ]
    : currentLang === 'de'
      ? [
          { factor: 'Plattform', check: 'Funktionen und Bedienung' },
          { factor: 'Produkte', check: 'Aktuelle Informationen' },
          { factor: 'QC', check: 'Fotos und Inspektion' },
          { factor: 'Lager', check: 'Lagerung' },
          { factor: 'Versand', check: 'Methoden und Kosten' },
          { factor: 'Support', check: 'Aktuelle Bedingungen' },
          { factor: 'Sicherheit', check: 'Domain und Zahlung' },
        ]
      : [
          { factor: 'Plataforma', check: 'Recursos e funcionamento' },
          { factor: 'Produtos', check: 'Informações atuais' },
          { factor: 'QC', check: 'Fotos e inspeção' },
          { factor: 'Warehouse', check: 'Armazenamento' },
          { factor: 'Shipping', check: 'Métodos e custos' },
          { factor: 'Suporte', check: 'Condições atuais' },
          { factor: 'Segurança', check: 'Domínio e pagamento' },
        ];

  const qcChecks = t('qc.checks').split(', ');

  return (
    <>
      {/* Shipping */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Stacked%20cardboard%20shipping%20boxes%20with%20kraft%20tape%20and%20shipping%20labels%20international%20logistics%20flat%20lay%20minimal%20clean%20soft%20light%20warm%20tones&width=800&height=600&seq=shipping-section&orientation=landscape&nocache=true"
                alt="CSSBuy shipping"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                {t('shipping.h2')}
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-6">
                {t('shipping.text')}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center px-5 py-2.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors text-sm"
              >
                {t('shipping.cta')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* QC */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
            {t('qc.h2')}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {qcChecks.map((check, i) => (
              <div key={i} className="bg-background-100 rounded-xl p-4 border border-background-200/70 flex items-center gap-2">
                <i className="ri-check-line text-primary-600" />
                <span className="text-sm font-medium text-foreground-800">{check}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2.5 bg-secondary-500 text-background-50 font-medium rounded-lg hover:bg-secondary-600 transition-colors text-sm"
            >
              {t('qc.cta')}
            </a>
          </div>
        </div>
      </section>

      {/* Review Table */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
            {t('review.h2')}
          </h2>
          <div className="overflow-x-auto rounded-xl border border-background-200/70 bg-background-50">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/70 bg-background-100">
                  <th className="text-left px-5 py-3 font-semibold text-foreground-800">{t('review.table.factor')}</th>
                  <th className="text-left px-5 py-3 font-semibold text-foreground-800">{t('review.table.check')}</th>
                </tr>
              </thead>
              <tbody>
                {reviewRows.map((row, i) => (
                  <tr key={i} className="border-b border-background-200/70 last:border-0">
                    <td className="px-5 py-3 text-foreground-700 font-medium">{row.factor}</td>
                    <td className="px-5 py-3 text-foreground-600">{row.check}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-center mt-8">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors text-sm"
            >
              {t('review.cta')}
            </a>
          </div>
        </div>
      </section>

      {/* Safety */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 rounded-2xl bg-accent-100 flex items-center justify-center text-accent-700 mx-auto mb-6">
              <i className="ri-shield-check-line text-2xl" />
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              {t('safety.h2')}
            </h2>
            <p className="text-foreground-700 leading-relaxed mb-6">
              {t('safety.text')}
            </p>
            <Link
              to="/article/cssbuy-spreadsheet-guide"
              className="inline-flex items-center px-5 py-2.5 bg-accent-500 text-background-50 font-medium rounded-lg hover:bg-accent-600 transition-colors text-sm"
            >
              {t('safety.cta')}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}